/**
 * Artifact gallery and Compare/Pick.
 *
 *  - **Encrypted accounts**: the list comes from sealed `artifact` index entities (written by the
 *    endpoint that uploaded each artifact) and every thumbnail is decrypted here via
 *    `BrowserArtifacts`; no signed URL or plaintext index exists. Compare/Pick is a plaintext hub
 *    feature and is not offered.
 *  - **Legacy accounts**: `GET /api/artifacts?projectId|runId|taskId` with signed URLs.
 *  - **Compare**: select 2–26 artifacts → `POST /api/artifacts/compare` → a Compare card (also in
 *    the Inbox) labelled A, B1, B2… (one letter per group, numbered within it).
 *  - **Pick**: tap labels or type "A, A, C1" → `POST /api/cards/:id/pick` returns one
 *    `run.message` per owning run; this device signs and sends them, so each agent continues
 *    with the picks.
 */
import { useQuery } from '@tanstack/react-query'
import { ArtifactThumb, Button } from '@viko/transcript-ui'
import { Columns2, Download, Send } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { isEncryptedUnavailable } from '../lib/content-errors.js'
import { type BrowserArtifacts, previewType } from '../lib/encrypted-artifacts.js'
import { qk, useRuns } from '../lib/queries.js'
import type { Artifact, CompareItem } from '../lib/types.js'
import { AutoArtifactGroup, isAutoArtifact } from './ArtifactGroups.js' // auto-artifacts
import { BeforeAfterSection, splitPaired } from './BeforeAfter.js' // visual-review
import { useOpenedArtifact } from './EncryptedArtifact.js'
import { ReviewServers } from './ReviewServers.js' // visual-review
import '../routes/project-views.css'
import { Empty, errorText, SkeletonRows, useToast } from './ui.js'

/** Gallery grouping: artifacts by `group` (else run), oldest first. */
export function groupArtifacts(
  list: readonly Artifact[],
  runTitle: (runId: string) => string | undefined = () => undefined,
): { key: string; title: string; items: Artifact[] }[] {
  const groups = new Map<string, Artifact[]>()
  for (const a of [...list].sort((x, y) => x.createdAt - y.createdAt)) {
    // auto-artifacts: a gallery/comparison, and every version of one claude.ai page, stay together.
    const k = a.collection
      ? `col:${a.collection.id}`
      : a.external?.url
        ? `claude:${a.external.url}`
        : (a.group ?? `run:${a.runId ?? 'none'}`)
    groups.set(k, [...(groups.get(k) ?? []), a])
  }
  return [...groups.entries()].map(([key, items]) => ({
    key,
    title:
      items[0]?.collection?.title ??
      items.at(-1)?.external?.title ??
      items[0]?.group ??
      items[0]?.title ??
      (items[0]?.runId ? runTitle(items[0].runId) : undefined) ??
      'Artifacts',
    items,
  }))
}

/** Signed URL for an artifact (and its poster), fetched lazily. */
function useArtifactUrl(id: string | undefined, enabled = true) {
  const b = useBackend()
  return useQuery({
    queryKey: ['artifact-url', id],
    queryFn: () => b.api.artifactUrl(id as string),
    enabled: !!id && enabled,
    staleTime: 4 * 60_000,
    retry: false,
  })
}

export function Thumb({
  a,
  label,
  selected,
  onToggle,
  toggleLabel,
}: {
  a: Pick<Artifact, 'id' | 'name' | 'title' | 'mime' | 'bytes' | 'caption' | 'uploaded' | 'thumbId'>
  label?: string
  selected?: boolean
  onToggle?: () => void
  toggleLabel?: string
}) {
  const media = !!a.mime && /^(image|video)\//.test(a.mime)
  const url = useArtifactUrl(a.id, a.uploaded && media)
  const thumb = useArtifactUrl(a.thumbId, a.uploaded)
  const name = a.title ?? a.name ?? a.id
  return (
    <div className={`gallery__cell${selected ? ' is-selected' : ''}`}>
      <ArtifactThumb
        artifact={{
          name,
          ...(a.mime ? { mime: a.mime } : {}),
          ...(url.data?.url ? { url: url.data.url } : {}),
          ...(thumb.data?.url ? { thumbUrl: thumb.data.url } : {}),
          ...(a.bytes ? { bytes: a.bytes } : {}),
          ...(a.caption ? { caption: a.caption } : {}),
        }}
        state={a.uploaded ? 'ok' : 'streaming'}
        {...(label ? { label } : {})}
      />
      {onToggle ? (
        <label className="gallery__pick">
          <input type="checkbox" checked={!!selected} onChange={onToggle} />
          <span>{toggleLabel ?? `Select ${name}`}</span>
        </label>
      ) : null}
    </div>
  )
}

/** Inline previews decrypt the full image only when it is small; larger ones wait for a tap. */
const INLINE_PREVIEW_BYTES = 4 * 1024 * 1024

/**
 * Encrypted tile: the poster (thumbnail, or a small image itself) is decrypted on mount; the full
 * media when opened. Non-previewable types (HTML, SVG, PDF, …) are download-only (S05).
 */
export function EncryptedThumb({ a, reader }: { a: Artifact; reader: BrowserArtifacts }) {
  const toast = useToast()
  const name = a.title ?? a.name ?? a.id
  const preview = a.mime ? previewType(a.mime) : undefined
  const posterRef =
    a.thumbEncrypted ??
    (preview?.startsWith('image/') && (a.bytes ?? 0) <= INLINE_PREVIEW_BYTES
      ? a.encrypted
      : undefined)
  const poster = useOpenedArtifact(reader, posterRef)
  const [opened, setOpened] = useState(false)
  const full = useOpenedArtifact(
    reader,
    opened && preview && posterRef !== a.encrypted ? a.encrypted : undefined,
  )
  const posterUrl = poster?.handle?.preview?.startsWith('image/') ? poster.handle.url : undefined
  const url = posterRef === a.encrypted ? posterUrl : full?.handle?.url
  const download = async () => {
    if (!a.encrypted) return
    try {
      const h = await reader.open(a.encrypted)
      const link = document.createElement('a')
      link.href = h.url
      link.download = h.name
      link.click()
      setTimeout(h.dispose, 60_000)
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <div className="gallery__cell" onClickCapture={() => setOpened(true)}>
      <ArtifactThumb
        artifact={{
          name,
          ...(a.mime ? { mime: preview ?? a.mime } : {}),
          ...(preview && url ? { url } : {}),
          ...(posterUrl ? { thumbUrl: posterUrl } : {}),
          ...(a.bytes ? { bytes: a.bytes } : {}),
          ...(a.caption ? { caption: a.caption } : {}),
        }}
        state={poster?.failed || full?.failed ? 'error' : 'ok'}
      />
      {preview ? null : (
        <Button
          size="sm"
          variant="ghost"
          icon={<Download size={14} />}
          onClick={() => void download()}
          aria-label={`Download ${name}`}
        >
          Download
        </Button>
      )}
    </div>
  )
}

export function ArtifactGallery({
  bare,
  ...q
}: {
  projectId?: string
  runId?: string
  taskId?: string
  /** Inside a project view's sections: no outer padding, section heads in the view's style. */
  bare?: boolean
}) {
  const b = useBackend()
  const toast = useToast()
  const encrypted = !!b.content
  const access = b.artifactAccess
  const list = useQuery({
    queryKey: encrypted ? [...qk.artifacts(q), access.accountId] : qk.artifacts(q),
    queryFn: () => b.api.artifacts(q),
    retry: false,
  })
  const [picked, setPicked] = useState<string[]>([])
  const [compare, setCompare] = useState<{ cardId: string; items: CompareItem[] } | null>(null)
  const runs = useRuns()
  // ---- visual-review: captures pinned to commits pair up before/after; the rest group as before ----
  const paired = useMemo(() => splitPaired(list.data ?? []), [list.data])
  const groups = useMemo(() => {
    const titles = new Map((runs.data ?? []).map((r) => [r.runId, r.title]))
    return groupArtifacts(paired.rest, (runId) => titles.get(runId))
  }, [paired.rest, runs.data])
  const reviewServers = q.projectId ? <ReviewServers projectId={q.projectId} /> : null
  const beforeAfter = paired.pairs.length ? (
    <BeforeAfterSection pairs={paired.pairs} access={access} />
  ) : null
  // ---- end visual-review ----

  if (list.isPending) return <SkeletonRows n={2} />
  if (list.isError)
    return isEncryptedUnavailable(list.error) ? (
      <Empty title="Artifacts aren’t available for this account yet">
        This account is end-to-end encrypted, and this server can’t list its artifacts. Screenshots
        and files still appear inside each session.
      </Empty>
    ) : (
      <Empty title="Could not load artifacts">{errorText(list.error)}</Empty>
    )
  if (!groups.length && !paired.pairs.length)
    return (
      <>
        {reviewServers ? (
          <div className={bare ? 'pv-art' : 'px-4 pt-3'}>{reviewServers}</div>
        ) : null}
        {bare ? (
          <p className="pv-empty">
            No artifacts yet. Screenshots, renders and files that agents post show here.
          </p>
        ) : (
          <Empty title="No artifacts yet">
            Artifacts from sessions appear here: screenshots, renders and files that agents post.
          </Empty>
        )}
      </>
    )
  if (encrypted)
    return (
      <div className={bare ? 'pv-art' : 'grid gap-4 px-4 pt-3 pb-8'}>
        {reviewServers}
        {beforeAfter}
        {groups.map((g) => (
          <section key={g.key} aria-label={g.title} className="pv-sec">
            <h3 className="pv-h">
              {g.title} <span className="pv-h__count">{g.items.length}</span>
            </h3>
            {g.items.some(isAutoArtifact) ? (
              <AutoArtifactGroup items={g.items} access={access} />
            ) : (
              <div className="gallery">
                {g.items.map((a) =>
                  access.reader && a.encrypted ? (
                    <EncryptedThumb key={a.id} a={a} reader={access.reader} />
                  ) : (
                    <div key={a.id} className="gallery__cell" role="status">
                      Locked. Unlock Viko on this device to view it.
                    </div>
                  ),
                )}
              </div>
            )}
          </section>
        ))}
      </div>
    )

  const toggle = (id: string) =>
    setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const startCompare = async () => {
    try {
      const r = await b.api.compareArtifacts({ artifactIds: picked })
      setCompare({ cardId: r.cardId, items: r.items })
      setPicked([])
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  if (compare)
    return (
      <div className={bare ? 'pv-art' : 'grid gap-3 px-4 pt-3 pb-8'}>
        <ComparePick
          cardId={compare.cardId}
          items={compare.items}
          onDone={() => setCompare(null)}
          onCancel={() => setCompare(null)}
        />
      </div>
    )

  return (
    <div className={bare ? 'pv-art' : 'grid gap-4 px-4 pt-3 pb-8'}>
      {reviewServers}
      {beforeAfter}
      {groups.map((g) => (
        <section key={g.key} aria-label={g.title} className="pv-sec">
          <h3 className="pv-h">
            {g.title} <span className="pv-h__count">{g.items.length}</span>
          </h3>
          <div className="gallery">
            {g.items.map((a) => (
              <Thumb
                key={a.id}
                a={a}
                selected={picked.includes(a.id)}
                onToggle={() => toggle(a.id)}
                toggleLabel={`Compare ${a.title ?? a.name ?? a.id}`}
              />
            ))}
          </div>
        </section>
      ))}
      {picked.length ? (
        <div className="gallery__bar" role="region" aria-label="Selection">
          <span className="flex-1 text-[14px]">{picked.length} selected</span>
          <Button
            size="sm"
            variant="primary"
            icon={<Columns2 size={14} />}
            disabled={picked.length < 2}
            onClick={() => void startCompare()}
          >
            Compare
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * A Compare card: labelled thumbnails and a reply. Tapping a thumbnail adds its label; the reply
 * accepts anything the hub parses ("A, A, C1": one pick per question, in order).
 */
export function ComparePick({
  cardId,
  items,
  title,
  onDone,
  onCancel,
}: {
  cardId: string
  items: CompareItem[]
  title?: string
  onDone?: () => void
  onCancel?: () => void
}) {
  const b = useBackend()
  const toast = useToast()
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState(false)
  const add = (label: string) =>
    setReply((r) => (r.trim() ? `${r.replace(/[\s,]+$/, '')}, ${label}` : label))
  const send = async () => {
    setBusy(true)
    try {
      const r = await b.api.pick(cardId, reply)
      for (const c of r.commands) await b.signPrepared(c)
      toast(
        r.commands.length
          ? `Sent “${reply.trim()}” to ${r.commands.length} session${r.commands.length === 1 ? '' : 's'}`
          : 'Picks recorded',
      )
      onDone?.()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="grid gap-3" aria-label="Compare and pick">
      <h3 className="m-0 text-[15px] font-semibold">
        {title ?? `Compare ${items.map((i) => i.label).join(' · ')}`}
      </h3>
      <div className="gallery">
        {items.map((it) => (
          <div key={it.artifactId} className="grid gap-1">
            <Thumb
              a={{
                id: it.artifactId,
                name: it.name,
                title: it.name,
                mime: it.mime ?? null,
                bytes: null,
                caption: null,
                uploaded: true,
              }}
              label={it.label}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => add(it.label)}
              aria-label={`Pick ${it.label}`}
            >
              Pick {it.label}
            </Button>
          </div>
        ))}
      </div>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (reply.trim()) void send()
        }}
      >
        <input
          className="input !min-h-10 min-w-0 flex-1"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder='Your picks, e.g. "A, A, C1"'
          aria-label="Picks"
        />
        {onCancel ? (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Back
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="primary"
          type="submit"
          icon={<Send size={14} />}
          disabled={busy || !reply.trim()}
        >
          Send picks
        </Button>
      </form>
    </section>
  )
}
